import React from 'react'
import PropTypes from 'prop-types'

import Icon from 'app/widgets/Icon'
import BlurProtection from '~/modules/BlurProtection'

import './style.scss'

const CENTER_POSITION = { x: 0.5, y: 0.5 }
const HINT_TEXT = 'Toque e arraste para revelar a foto'

/**
 * Renders the enlarged photo partially blurred. Two interaction styles, depending on
 * `mode` (see BlurProtection.usesDragInteraction):
 * - diagonal/vertical/horizontal: a tap flips which half is revealed.
 * - circular/band ("faixa"): dragging the finger moves the revealed area (a lens or a
 *   strip) across the photo, like a magnifying glass. A hint bubble explains this until
 *   the first drag, and circular also gets a white ring tracing the lens edge.
 *
 * Made of two full-bleed <img> layers instead of a canvas: the base thumb, blurred, and
 * the full-quality photo clipped to the revealed area.
 *
 * Drag tracking uses Pointer Events only (no separate touch fallback): Pointer Events
 * cover touch and mouse uniformly on every target browser (iOS Safari 13+, Chrome
 * Android). Mixing in touchstart/touchmove as well double-fires per touch and, worse,
 * `TouchEvent` has no `clientX`/`clientY` of its own (only `touches[0]`'s), so reading
 * them off it silently produces NaN and breaks the clip-path (see git history).
 */
class BlurProtectedImage extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      isFlipped: false,
      position: CENTER_POSITION,
      hasInteracted: false,
      containerSize: { width: 0, height: 0 }
    }

    this.containerRef = React.createRef()

    this.handleToggle = this.handleToggle.bind(this)
    this.handleDragStart = this.handleDragStart.bind(this)
    this.handleDragMove = this.handleDragMove.bind(this)
  }

  componentDidMount() {
    if (!this.isDragMode()) return

    // Only needed to size the circular mode's ring in exact pixels (see
    // BlurProtection.circleRadiusPx) -- everything else here is percentage-based and
    // doesn't care about the container's actual size.
    this.measureContainer()

    if (typeof ResizeObserver === 'undefined') return
    this.resizeObserver = new ResizeObserver(this.measureContainer.bind(this))
    this.resizeObserver.observe(this.containerRef.current)
  }

  componentWillUnmount() {
    if (this.resizeObserver) this.resizeObserver.disconnect()
  }

  isDragMode() {
    return BlurProtection.usesDragInteraction(this.props.mode)
  }

  measureContainer() {
    const container = this.containerRef.current
    if (!container) return

    this.setState({
      containerSize: {
        width: container.clientWidth,
        height: container.clientHeight
      }
    })
  }

  handleToggle() {
    if (this.isDragMode()) return
    this.setState((prevState) => ({ isFlipped: !prevState.isFlipped }))
  }

  updatePositionFromClientPoint(clientX, clientY) {
    if (typeof clientX !== 'number' || typeof clientY !== 'number') return
    if (Number.isNaN(clientX) || Number.isNaN(clientY)) return

    const container = this.containerRef.current
    if (!container) return

    const rect = container.getBoundingClientRect()
    if (!rect.width || !rect.height) return

    this.setState({
      hasInteracted: true,
      position: {
        x: Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)),
        y: Math.min(1, Math.max(0, (clientY - rect.top) / rect.height))
      }
    })
  }

  handleDragStart(event) {
    if (!this.isDragMode()) return
    this.updatePositionFromClientPoint(event.clientX, event.clientY)
  }

  handleDragMove(event) {
    if (!this.isDragMode()) return
    // Only react to a mouse drag (buttons === 0 means the button is up); touch pointer
    // moves always carry position info regardless of `buttons`.
    if (event.pointerType === 'mouse' && event.buttons === 0) return
    this.updatePositionFromClientPoint(event.clientX, event.clientY)
  }

  renderRing() {
    if (this.props.mode !== 'circular') return null

    const { width, height } = this.state.containerSize
    const radius = BlurProtection.circleRadiusPx(width, height)
    if (!radius) return null

    const diameter = radius * 2
    const { x, y } = this.state.position

    return (
      <div
        className="blur-protected-image__ring"
        style={{
          width: `${diameter}px`,
          height: `${diameter}px`,
          left: `${x * 100}%`,
          top: `${y * 100}%`
        }}
      />
    )
  }

  renderHint() {
    if (!this.isDragMode() || this.state.hasInteracted) return null

    return (
      <div className="blur-protected-image__hint">
        <Icon.Tap />
        <span>{HINT_TEXT}</span>
      </div>
    )
  }

  render() {
    const { picture, mode, intensity, imgRef, onLoad, onError } = this.props
    const clipPath = BlurProtection.buildClipPath(mode, {
      isFlipped: this.state.isFlipped,
      position: this.state.position
    })

    return (
      <div
        className="blur-protected-image"
        ref={this.containerRef}
        onClick={this.handleToggle}
        onPointerDown={this.handleDragStart}
        onPointerMove={this.handleDragMove}
      >
        <img
          className="blur-protected-image__base"
          src={picture.thumb}
          draggable={false}
          style={{ filter: BlurProtection.buildBlurFilter(intensity) }}
        />
        <img
          className="blur-protected-image__sharp"
          src={picture.image}
          ref={imgRef}
          draggable={false}
          onLoad={onLoad}
          onError={onError}
          style={{ clipPath }}
        />
        {this.renderRing()}
        {this.renderHint()}
      </div>
    )
  }
}

BlurProtectedImage.propTypes = {
  picture: PropTypes.shape({
    image: PropTypes.string,
    thumb: PropTypes.string
  }).isRequired,
  mode: PropTypes.oneOf(BlurProtection.MODES).isRequired,
  intensity: PropTypes.number.isRequired,
  imgRef: PropTypes.any,
  onLoad: PropTypes.func,
  onError: PropTypes.func
}

export default BlurProtectedImage
